import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map, has, startsWith, findIndex, isObject } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import PropertyApi from '@/extensions/shop/store/api/property';
import PropTypeApi from '@/extensions/shop/store/api/propType';
import CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import TaxApi from '@/extensions/shop/store/api/tax';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

// Above this the option table is served by the paginated query rather than held in
// the form. Production has properties with 16,802 options; 450 of 680 have 20 or fewer.
export const LARGE_VALUE_OPTION_SET = 100;

const defaultForm = {
    key: '',
    name: '',
    component: undefined,
    filter_component: undefined,
    unit: undefined,
    input_type: undefined,
    min_rows: undefined,
    max_rows: undefined,
    min_input: undefined,
    max_input: undefined,
    max_file_size: undefined,
    allowed_mime_types: undefined,
    datetype: undefined,
    article_required: false,
    is_required: true,
    visible_on_detail_page: false,
    visible_on_product_listing: false,
    is_sortable: false,
    is_comparable: false,
    use_in_search: false,
    propType: undefined,
    translationOrigin: undefined,
    translations: [],
    valueOptions: [],
    prices: [],
    // The trigger property and the trigger's options that reveal this property.
    conditionProperty: null,
    conditionValueOptions: [],
};

const defaultUi = {
    pagination: {},
    form: {
        initialized: false,
        validateOnEnter: false,
    },
};

export const useShopPropertyStore = defineStore('shopProperty', {
    state: () => ({
        loading: true,
        pristineValueOptions: {},
        // Server-side option list, used instead of the in-form array once a property
        // holds more options than a browser should be asked to render.
        valueOptionsPaginated: {
            variables: { paginationSizes: [25, 50, 100] },
            filters: {},
            pageInfo: { total: 0, perPage: 25, currentPage: 1, lastPage: 0 },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            order: null,
            direction: 'asc',
            items: [],
            loading: false,
            selection: [],
        },
        propertiesPaginated: {
            variables: {
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            defaultSort: null,
            items: [],
        },
        options: {
            dateType: {
                loading: false,
                items: [
                    { value: 'datetime', label: 'Datum, Uhrzeit' },
                    { value: 'date', label: 'Datum' },
                    { value: 'week', label: 'Woche' },
                    { value: 'month', label: 'Monat' },
                    { value: 'year', label: 'Jahr' },
                ],
            },
            inputType: {
                loading: false,
                items: [
                    { value: 'textfield', label: 'Textzeile' },
                    { value: 'textarea', label: 'Textblock' },
                    { value: 'number', label: 'Zahl' },
                ],
            },
            propType: {
                loading: false,
                items: [],
            },
            customerGroup: {
                loading: false,
                items: [],
            },
            tax: {
                loading: false,
                items: [],
            },
            priceType: {
                loading: false,
                items: [
                    { value: 'main', label: 'Standard' },
                    { value: 'group', label: 'Kundengruppenpreis' },
                ],
            },
        },
        form: cloneDeep(defaultForm),
        ui: cloneDeep(defaultUi),
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter'),
        showRelatedArticles: () => false,
        rules: (state) => {
            const setupStore = useSetupStore(pinia);

            let rules = {
                key: 'required',
                name: 'required|min:3',
                propType: { id: 'required' },
            };

            const shop = setupStore.activeCms.shop;
            const property = state.form;

            if (property.id) {
                rules.key = `required|unique_property_key:"shop":${shop.id},"property":${property.id}`;
            } else {
                rules.key = `required|unique_property_key:"shop":${shop.id}`;
            }

            if (property.propType && property.propType.key === 'unit') {
                rules.unit = 'required';
            } else if (property.propType && property.propType.key === 'date') {
                rules.datetype = 'required';
            }

            // A trigger without any revealing option would hide the property for good.
            if (get(property, 'conditionProperty.id')) {
                rules.conditionValueOptions = 'required';
            }

            if (state.form.prices && state.form.prices.length) {
                rules.prices = [];
                state.form.prices.forEach((price, index) => {
                    rules.prices.push({
                        type: 'required',
                        value: 'required|min_value:0',
                        min_qty: 'required|integer|min_value:1',
                        start_at: 'required',
                        end_at: 'required',
                        customerGroup: { id: 'required' },
                    });
                });
            }

            return rules;
        },
    },

    actions: {
        addPaginatedPropertiesToState(data) {
            this.propertiesPaginated.items = data.items;
            this.propertiesPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.propertiesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.propertiesPaginated.link = cloneDeep(data.config.link);
            this.propertiesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
            this.propertiesPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.propertiesPaginated.allColumns, (column) => {
                let allowed = find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.propertiesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.propertiesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.propertiesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                value.in.length === 0
            ) {
                Vue.delete(this.propertiesPaginated.filters, key);
            } else {
                Vue.set(this.propertiesPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.propertiesPaginated.variables, 'order', key);
            Vue.set(this.propertiesPaginated.variables, 'direction', value);
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.propertiesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.propertiesPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.propertiesPaginated, 'items') || [];
            const ids = items.map((el) => parseInt(el.id));

            let selection = this.propertiesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(
                    selection,
                    'identifiers',
                    selection.identifiers.filter((el) => ids.includes(el) === false)
                );
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.propertiesPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map((el) => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map((el) => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.propertiesPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        updateLoading(value) {
            this.loading = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.propertiesPaginated, 'showFilters', value);
        },

        resetUI() {
            Vue.set(this.ui.form, 'initialized', false);
        },

        insertProperty(property) {
            Vue.set(this, 'form', property);
            this.snapshotValueOptions(property);
        },

        // A property can hold tens of thousands of options, and re-sending the
        // untouched ones costs one round of queries each on the server. Remember what
        // arrived so the save can carry only what actually moved.
        snapshotValueOptions(property) {
            const snapshot = {};
            (get(property, 'valueOptions') || []).forEach((option) => {
                if (option && option.id) snapshot[option.id] = JSON.stringify(option);
            });
            Vue.set(this, 'pristineValueOptions', snapshot);
        },

        // An option pulled in from a page joins the form before anyone has touched it.
        // Recording it as it arrived is what keeps "opened the drawer" from counting as
        // an unsaved change - and keeps the save from re-sending an untouched option.
        snapshotValueOption(option) {
            const id = get(option, 'id');
            if (!id || startsWith(String(id), 'new:')) return;

            Vue.set(this.pristineValueOptions, id, JSON.stringify(option));
        },

        changedValueOptions(valueOptions) {
            const pristine = this.pristineValueOptions || {};

            return (valueOptions || []).filter((option) => {
                const id = get(option, 'id');
                // new, deleted, or absent from the snapshot: always send
                if (!id || startsWith(String(id), 'new:') || option.deleted) return true;
                if (!has(pristine, id)) return true;

                return pristine[id] !== JSON.stringify(option);
            });
        },

        updatePropTypeOptionsLoading(value) {
            Vue.set(this.options.propType, 'loading', value);
        },

        insertPropTypeOptions(propTypes) {
            Vue.set(this.options.propType, 'items', propTypes);
        },

        updateCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.customerGroup, 'loading', value);
        },

        insertCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.customerGroup, 'items', customerGroups);
        },

        updateTaxOptionsLoading(value) {
            Vue.set(this.options.tax, 'loading', value);
        },

        insertTaxOptions(taxes) {
            Vue.set(this.options.tax, 'items', taxes);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormKey(value) {
            Vue.set(this.form, 'key', value);
        },

        setFormName(value) {
            Vue.set(this.form, 'name', value);
        },

        setFormPropType(value) {
            Vue.set(this.form, 'propType', value);
        },

        setFormDatetype(value) {
            Vue.set(this.form, 'datetype', value);
        },

        setFormUnit(value) {
            Vue.set(this.form, 'unit', value);
        },

        setFormInputType(value) {
            Vue.set(this.form, 'input_type', value);
        },

        setFormMinRows(value) {
            Vue.set(this.form, 'min_rows', value);
        },

        setFormMaxRows(value) {
            Vue.set(this.form, 'max_rows', value);
        },

        setFormMinInput(value) {
            Vue.set(this.form, 'min_input', value);
        },

        setFormMaxInput(value) {
            Vue.set(this.form, 'max_input', value);
        },

        setFormMaxFileSize(value) {
            Vue.set(this.form, 'max_file_size', value);
        },

        setFormAllowedMimeTypes(value) {
            Vue.set(this.form, 'allowed_mime_types', value);
        },

        setFormArticleRequired(value) {
            Vue.set(this.form, 'article_required', value);
        },

        setFormIsRequired(value) {
            Vue.set(this.form, 'is_required', value);
        },

        setFormComponent(value) {
            Vue.set(this.form, 'component', value);
        },

        setFormFilterComponent(value) {
            Vue.set(this.form, 'filter_component', value);
        },

        setFormVisibleOnDetailPage(value) {
            Vue.set(this.form, 'visible_on_detail_page', value);
        },

        setFormVisibleOnProductListing(value) {
            Vue.set(this.form, 'visible_on_product_listing', value);
        },

        setFormIsSortable(value) {
            Vue.set(this.form, 'is_sortable', value);
        },

        setFormIsComparable(value) {
            Vue.set(this.form, 'is_comparable', value);
        },

        setFormUseInSearch(value) {
            Vue.set(this.form, 'use_in_search', value);
        },

        setFormIsFilterableByGroup(value) {
            Vue.set(this.form, 'is_filterable_by_group', value);
        },

        setFormConditionProperty(value) {
            const previousId = get(this.form, 'conditionProperty.id');
            Vue.set(this.form, 'conditionProperty', value || null);
            // The options belong to the trigger; another trigger has other options.
            if (get(value, 'id') !== previousId) Vue.set(this.form, 'conditionValueOptions', []);
        },

        setFormConditionValueOptions(value) {
            Vue.set(this.form, 'conditionValueOptions', value || []);
        },

        setFormValueOptions(value) {
            Vue.set(this.form, 'valueOptions', value);
        },

        setFormValueOption(value) {
            const i = this.form.valueOptions.findIndex((el) => el.id === value.id);
            if (i >= 0) Vue.set(this.form.valueOptions, i, value);
            else Vue.set(this.form.valueOptions, this.form.valueOptions.length, value);
        },

        addFormPrice(price) {
            const index = get(this, 'form.prices.length', 0);
            if (!price.id) price.id = uniqid('new:');
            Vue.set(this.form.prices, index, price);
        },

        updateFormPrice({ price, field, value }) {
            const prices = get(this.form, 'prices') || [];
            const index = findIndex(prices, { id: price.id });

            switch (field) {
                case 'tax':
                case 'customerGroup':
                    if (!isObject(value)) value = null;
                    break;
                case 'value':
                case 'net_value':
                case 'price':
                case 'min_qty':
                    if (isNaN(value)) value = null;
                    break;
            }

            if (index >= 0) {
                Vue.set(this.form.prices[index], field, value);
            }
        },

        deleteFormPrice({ index }) {
            Vue.delete(this.form.prices, index);
        },

        addFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex((el) => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translationOrigin.translations, i, translation);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex((el) => el.language.code === translation.language.code);
                if (i < 0) i = translations.length;
                Vue.set(this.form.translations, i, translation);
            }
        },

        removeFormTranslation(translation) {
            if (this.form.translationOrigin) {
                const translations = get(this.form.translationOrigin, 'translations', []);
                let i = translations.findIndex((el) => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translationOrigin.translations, i);
            } else {
                const translations = get(this.form, 'translations', []);
                let i = translations.findIndex((el) => el.language.code === translation.language.code);
                if (i >= 0) Vue.delete(this.form.translations, i);
            }
        },

        // Async actions
        fetchPropertiesPaginated() {
            return PropertyApi.fetchPropertiesPaginated(this.propertiesPaginated).then((data) => {
                this.addPaginatedPropertiesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction(action) {
            return PropertyApi.processPropertyPaginationBulkAction(action, this.propertiesPaginated);
        },

        // A property with more options than the table would ever show at once is
        // fetched without them: its list is served page by page instead. The count
        // comes back either way, so one round trip settles which shape this is.
        fetchProperty(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (!cms || !cms.shop || !cms.shop.id) return Promise.resolve();

            return PropertyApi.fetchProperty(id, cms.shop.id, false).then((property) => {
                const count = get(property, 'value_option_count') || 0;

                if (count > LARGE_VALUE_OPTION_SET) {
                    this.insertProperty(cloneDeep({ ...property, valueOptions: [] }));

                    return this.fetchValueOptionsPage(id);
                }

                return PropertyApi.fetchProperty(id, cms.shop.id, true).then((full) => {
                    this.insertProperty(cloneDeep(full));
                });
            });
        },

        fetchPropTypeOptions() {
            this.updatePropTypeOptionsLoading(true);
            return PropTypeApi.fetchList()
                .then((propTypes) => {
                    this.updatePropTypeOptionsLoading(false);
                    this.insertPropTypeOptions(propTypes);
                })
                .catch((err) => {
                    this.updatePropTypeOptionsLoading(false);
                    throw err;
                });
        },

        fetchCustomerGroupOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeCms.shop.defaultCustomerGroup'))
                this.insertCustomerGroupOptions([setupStore.activeCms.shop.defaultCustomerGroup]);

            this.updateCustomerGroupOptionsLoading(true);
            return CustomerGroupApi.fetchList(shopID)
                .then((customerGroups) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    this.insertCustomerGroupOptions(customerGroups);
                })
                .catch((err) => {
                    this.updateCustomerGroupOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore(pinia);
            if (has(setupStore, 'activeShop.defaultTax')) this.insertTaxOptions([setupStore.activeShop.defaultTax]);

            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(shopID)
                .then((taxes) => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch((err) => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        // ---- server-side option list -------------------------------------------

        setValueOptionsPage(page) {
            const target = this.valueOptionsPaginated;
            Vue.set(target, 'items', page.items || []);
            Vue.set(target, 'pageInfo', page.pageInfo || target.pageInfo);
            if (page.config) {
                Vue.set(target, 'allColumns', page.config.allColumns || []);
                Vue.set(target, 'activeColumns', page.config.activeColumnKeys || []);
                Vue.set(target, 'showFilters', !!page.config.showFilters);
                Vue.set(target, 'defaultSort', page.config.defaultSort || null);
            }
        },

        fetchValueOptionsPage(propertyId) {
            const target = this.valueOptionsPaginated;
            Vue.set(target, 'loading', true);

            const filters = Object.keys(target.filters || {})
                .map((key) => ({ key, ...target.filters[key] }))
                .filter((filter) => Object.keys(filter).length > 1);

            return PropertyApi.fetchValueOptionsPaginated({
                property: parseInt(propertyId),
                currentPage: target.pageInfo.currentPage,
                perPage: target.pageInfo.perPage,
                order: target.order,
                direction: target.direction,
                filters,
                scope: 'ex',
                type: 'shop_property_value_option',
            })
                .then((page) => this.setValueOptionsPage(page))
                .finally(() => Vue.set(target, 'loading', false));
        },

        setValueOptionsFilter(key, value) {
            const filters = { ...this.valueOptionsPaginated.filters };
            if (value === null || value === undefined || value === '') delete filters[key];
            else filters[key] = value;
            Vue.set(this.valueOptionsPaginated, 'filters', filters);
            Vue.set(this.valueOptionsPaginated.pageInfo, 'currentPage', 1);
        },

        setValueOptionsSorting(order, direction = 'asc') {
            Vue.set(this.valueOptionsPaginated, 'order', order);
            Vue.set(this.valueOptionsPaginated, 'direction', direction);
            Vue.set(this.valueOptionsPaginated.pageInfo, 'currentPage', 1);
        },

        setValueOptionsPageNumber(page) {
            Vue.set(this.valueOptionsPaginated.pageInfo, 'currentPage', page);
        },

        setValueOptionsPerPage(perPage) {
            Vue.set(this.valueOptionsPaginated.pageInfo, 'perPage', perPage);
            Vue.set(this.valueOptionsPaginated.pageInfo, 'currentPage', 1);
        },

        setValueOptionsSelection(ids) {
            Vue.set(this.valueOptionsPaginated, 'selection', ids);
        },

        // One option, with everything the drawer edits. Used when the table came from
        // the paginated query and therefore carries only display fields.
        fetchSingleValueOption({ shopID, propertyId, valueField, id }) {
            if (!shopID || !propertyId || !valueField) return Promise.resolve(null);

            return PropertyApi.autocomplete(shopID, propertyId, valueField, null, 500).then(
                (options) => (options || []).find((option) => option.id == id) || null
            );
        },

        moveValueOption({ id, reference, placement }) {
            return PropertyApi.moveValueOption(id, reference, placement);
        },

        sortValueOptions({ id, order }) {
            return PropertyApi.sortValueOptions(id, order);
        },

        restoreValueOptionOrder(id) {
            return PropertyApi.restoreValueOptionOrder(id);
        },

        mergeValueOptions({ survivor, duplicates }) {
            return PropertyApi.mergeValueOptions(survivor, duplicates);
        },

        translate({ property, language, properties, type }) {
            return PropertyApi.translate(property, language, properties, type);
        },

        save(property) {
            const setupStore = useSetupStore(pinia);
            if (!property.id || startsWith(property.id, 'new:')) {
                property.shop = { id: setupStore.activeCms.shop.id };
                return this.create(property);
            } else {
                return this.update(property);
            }
        },

        create(property) {
            return PropertyApi.create(property).then(async (property) => {
                this.insertProperty(cloneDeep(property));
            });
        },

        update(property) {
            let translation = false;
            if (property.translationOrigin) translation = true;

            // The backend's valueOptions loop does not diff-delete, so an option left
            // out of the payload is left alone - which is what makes this safe.
            const payload = { ...property };
            if (Array.isArray(payload.valueOptions)) {
                payload.valueOptions = this.changedValueOptions(payload.valueOptions);
            }

            const isLarge = (get(property, 'value_option_count') || 0) > LARGE_VALUE_OPTION_SET;

            return PropertyApi.update(payload, translation, !isLarge).then(async (saved) => {
                this.insertProperty(cloneDeep(isLarge ? { ...saved, valueOptions: [] } : saved));
                if (isLarge) this.fetchValueOptionsPage(saved.id);
            });
        },

        delete(id) {
            return PropertyApi.delete(id).then(() => {
                this.reset();
                this.resetUI();
            });
        },

        cloneProperty(id) {
            return PropertyApi.clone(id);
        },

        clearUnlinkedValueOptions(id) {
            return PropertyApi.clearUnlinkedValueOptions(id).then(() => {
                return this.fetchProperty(id);
            });
        },

        exportValueOptions(id) {
            return PropertyApi.exportValueOptions(id);
        },

        validateValueOptionImportCsv({ id, file }) {
            return PropertyApi.validateValueOptionImportCsv(id, file);
        },

        importValueOptions({ id, file }) {
            return PropertyApi.importValueOptions(id, file).then((property) => {
                this.setFormValueOptions(cloneDeep(property.valueOptions || []));
            });
        },

        reset() {
            this.insertProperty(cloneDeep(defaultForm));
        },
    },
});
